import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { firebaseConfigValid } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, Eye, EyeOff, AlertTriangle, HelpCircle } from 'lucide-react';
import HelpModal from '@/components/HelpModal';

export default function LoginPage() {
  const { login, signup, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetErr, setResetErr] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetErr('');
    setResetMsg('');
    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetMsg('Se l\'email è registrata, riceverai a breve un link per reimpostare la password. Controlla anche la cartella spam.');
    } catch (err) {
      const code = err.code || '';
      if (code.includes('invalid-email')) {
        setResetErr('Email non valida');
      } else if (code.includes('user-not-found')) {
        setResetMsg('Se l\'email è registrata, riceverai a breve un link per reimpostare la password.');
      } else {
        setResetErr('Errore durante l\'invio. Riprova più tardi.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  if (!firebaseConfigValid) {
    return (
      <div className="min-h-screen bg-fw-light flex items-center justify-center p-8" data-testid="login-missing-config">
        <div className="max-w-lg w-full bg-white rounded-lg border border-fw-border shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-amber-100 rounded-lg"><AlertTriangle className="w-6 h-6 text-amber-600" /></div>
            <h1 className="font-heading text-2xl font-bold text-fw-dark">Firebase non configurato</h1>
          </div>
          <p className="text-fw-muted mb-4">Configura le variabili ambiente Firebase nel file <code className="bg-fw-light px-1.5 py-0.5 rounded text-sm font-mono">.env</code> per attivare il login.</p>
          <div className="bg-fw-light border border-fw-border rounded-md p-4 font-mono text-sm text-fw-dark space-y-1">
            <p>REACT_APP_FIREBASE_API_KEY=...</p>
            <p>REACT_APP_FIREBASE_AUTH_DOMAIN=...</p>
            <p>REACT_APP_FIREBASE_PROJECT_ID=...</p>
            <p>REACT_APP_FIREBASE_STORAGE_BUCKET=...</p>
            <p>REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...</p>
            <p>REACT_APP_FIREBASE_APP_ID=...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignup) {
        await signup(email, password);
      } else {
        await login(email, password);
      }
      navigate('/dashboard');
    } catch (err) {
      const code = err.code || '';
      if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
        setError('Email o password non validi');
      } else if (code.includes('email-already-in-use')) {
        setError('Email già registrata');
      } else if (code.includes('weak-password')) {
        setError('La password deve essere di almeno 6 caratteri');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      if (!err.code?.includes('popup-closed')) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1761461535556-9bec8112eaab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBibHVlJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjBhcmNoaXRlY3R1cmUlMjBvZmZpY2V8ZW58MHx8fHwxNzcxMjQyNjA1fDA&ixlib=rb-4.1.0&q=85"
          alt="Abstract architecture"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-heading text-4xl font-bold text-white mb-2" style={{textShadow: '0 2px 8px rgba(0,0,0,0.7)'}}>ETO Support</h2>
          <p className="text-white text-lg font-medium" style={{textShadow: '0 2px 6px rgba(0,0,0,0.7)'}}>Traccia le attività, monitora i KPI, ottimizza i processi.</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-8 bg-white">
        <div className="flex justify-center items-center gap-6 mb-4">
          <img src="/Logo_HQ.png" alt="HQ Engineering Italia" className="h-16 object-contain" />
          <img src="/Logo_Fastweb.png" alt="Fastweb + Vodafone" className="h-28 object-contain" />
        </div>
        <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h1 className="font-heading text-3xl font-bold text-fw-dark">
              {isSignup ? 'Crea account' : 'Accedi'}
            </h1>
            <p className="text-fw-muted mt-2">
              {isSignup ? 'Registrati per iniziare a tracciare le attività' : 'Inserisci le tue credenziali per accedere'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-fw-red/20 rounded-md text-sm text-fw-red" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-fw-dark block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="block w-full h-10 rounded-md border border-fw-border px-3 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none placeholder-fw-subtle"
                placeholder="nome@azienda.com"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-fw-dark block mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="block w-full h-10 rounded-md border border-fw-border px-3 pr-10 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none placeholder-fw-subtle"
                  placeholder="Minimo 6 caratteri"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-fw-subtle hover:text-fw-muted"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isSignup ? 'Registrati' : 'Accedi')}
            </Button>

            <div className={`flex items-center ${isSignup ? 'justify-start' : 'justify-between'}`}>
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="text-sm text-fw-blue hover:underline inline-flex items-center gap-1"
                data-testid="login-help-btn"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Serve aiuto?
              </button>
              {!isSignup && (
                <button
                  type="button"
                  onClick={() => { setResetOpen(true); setResetEmail(email); setResetMsg(''); setResetErr(''); }}
                  className="text-sm text-fw-blue hover:underline"
                  data-testid="forgot-password-btn"
                >
                  Password dimenticata?
                </button>
              )}
            </div>
          </form>

          <HelpModal
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            context="login"
            userEmail={email.trim() || null}
          />

          {resetOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="reset-password-modal">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <h2 className="font-heading text-xl font-bold text-fw-dark mb-2">Reimposta password</h2>
                <p className="text-sm text-fw-muted mb-4">Inserisci la tua email: riceverai un link per reimpostare la password.</p>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="block w-full h-10 rounded-md border border-fw-border px-3 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none"
                    placeholder="nome@azienda.com"
                    data-testid="reset-email-input"
                  />
                  {resetErr && <div className="p-3 bg-red-50 border border-fw-red/20 rounded-md text-sm text-fw-red">{resetErr}</div>}
                  {resetMsg && <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">{resetMsg}</div>}
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setResetOpen(false)} data-testid="reset-cancel-btn">Chiudi</Button>
                    <Button type="submit" disabled={resetLoading} className="bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark" data-testid="reset-submit-btn">
                      {resetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invia link'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-fw-border" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-sm text-fw-subtle">oppure</span></div>
          </div>

          <Button
            variant="outline"
            onClick={handleGoogle}
            disabled={loading}
            className="w-full h-10 border-fw-border text-fw-dark hover:bg-fw-light font-medium"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Accedi con Google
          </Button>

          <p className="text-center text-sm text-fw-muted mt-6">
            {isSignup ? 'Hai già un account?' : 'Non hai un account?'}{' '}
            <button
              onClick={() => { setIsSignup(!isSignup); setError(''); }}
              className="text-fw-blue hover:text-fw-blue font-medium"
              data-testid="toggle-auth-mode-btn"
            >
              {isSignup ? 'Accedi' : 'Registrati'}
            </button>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}

